import React from "react";
import ApiAuthCtrl from "../api/ApiAuthCtrl";
import ApiAdminCtrl from "../api/ApiAdminCtrl";
import {APP_NAME} from "../App";

export const AuthContext = React.createContext(null);

/**
 * Fournit auth, user, et les actions d'authentification à toute l'arborescence.
 * Inclut le refresh token automatique toutes les 15 secondes (extrait de App.js).
 */
export function AuthProvider({ children }) {
    const [auth, setAuth] = React.useState(null);
    const [user, setUser] = React.useState(null);
    const [pendingCount, setPendingCount] = React.useState(0);
    const [analyticsSummaries, setAnalyticsSummaries] = React.useState([]);

    // Ref stable vers user — évite les stale closures dans les setInterval
    const userRef = React.useRef(null);
    React.useEffect(() => { userRef.current = user; }, [user]);

    // Ref pour comparer les tokens sans provoquer de re-render
    const authRef = React.useRef(null);

    function updateStorage(data) {
        // Ne mettre à jour auth (et donc re-render l'arbre) que si le token a changé
        if (authRef.current?.access_token !== data?.access_token) {
            authRef.current = data;
            setAuth(data);
        }
        // pendingUsersCount est inclus directement dans la réponse du token
        if (typeof data?.pendingUsersCount === "number") {
            setPendingCount(prev => prev === data.pendingUsersCount ? prev : data.pendingUsersCount);
        }
        // analyticsSummaries : résumés légers des analyses inclus dans le token refresh
        if (Array.isArray(data?.analyticsSummaries)) {
            setAnalyticsSummaries(prev =>
                JSON.stringify(prev) === JSON.stringify(data.analyticsSummaries) ? prev : data.analyticsSummaries
            );
        }
    }

    function cleanUser() {
        setAuth(null);
        setUser(null);
        sessionStorage.removeItem("user");
        localStorage.removeItem("user");
    }

    function disconnect() {
        sessionStorage.removeItem("user");
        localStorage.removeItem("user");
        setUser(null);
        setAuth(null);
    }

    // Persister auth + charger userInfo
    React.useEffect(() => {
        if (auth) {
            sessionStorage.setItem("user", JSON.stringify(auth));
            localStorage.setItem("user", JSON.stringify(auth));
            if (!user) {
                ApiAuthCtrl.userInfo().then(({ data }) => {
                    if (data) setUser(data);
                });
            }
        }
    }, [auth, user]);

    // Référence stable vers l'ID du setInterval pour le refresh token.
    // Permet de le suspendre / reprendre depuis le listener visibilitychange.
    const intervalRef = React.useRef(null);

    // Init : restaurer la session et démarrer le refresh token toutes les 15 s.
    // Le polling est suspendu quand l'onglet est en arrière-plan (visibilitychange)
    // et reprend immédiatement dès que l'onglet redevient actif.
    React.useEffect(() => {
        sessionStorage.removeItem("user");

        // Compteur d'échecs consécutifs pour le refresh token.
        // On ne déconnecte qu'après plusieurs échecs consécutifs ou sur un 401 explicite.
        let consecutiveFailures = 0;
        const MAX_REFRESH_FAILURES = 3;

        async function refreshToken() {
            if (localStorage.getItem("user") !== null) {
                const token = JSON.parse(localStorage.getItem("user")).refresh_token;
                const { data, error } = await ApiAuthCtrl.login("refresh_token", undefined, undefined, token, null);
                if (data) {
                    consecutiveFailures = 0;
                    updateStorage(data);
                } else if (error) {
                    const status = error?.response?.status;
                    console.warn("[AuthContext] refreshToken failed", { status, consecutiveFailures: consecutiveFailures + 1, error });
                    // 401/403 = token invalide → déconnexion immédiate
                    if (status === 401 || status === 403) {
                        cleanUser();
                    } else {
                        // Erreur réseau ou transitoire → tolérer quelques échecs
                        consecutiveFailures++;
                        if (consecutiveFailures >= MAX_REFRESH_FAILURES) {
                            console.warn("[AuthContext] max refresh failures reached, disconnecting");
                            cleanUser();
                        }
                    }
                }
            }
        }

        const FOREGROUND_INTERVAL = 60 * 1000;      // 1 min en premier plan (refresh token = 24h, pas besoin de 15s)
        const BACKGROUND_INTERVAL = 10 * 60 * 1000; // 10 min en arrière-plan (refresh token = 24h)

        function startPolling(interval = FOREGROUND_INTERVAL) {
            stopPolling();
            intervalRef.current = setInterval(refreshToken, interval);
        }

        function stopPolling() {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }

        async function handleVisibilityChange() {
            if (document.visibilityState === 'hidden') {
                // Arrière-plan : polling réduit pour garder le token vivant
                startPolling(BACKGROUND_INTERVAL);
            } else {
                // L'onglet redevient visible : tenter refresh, puis access_token si échec
                const { data } = await ApiAuthCtrl.login(
                    "refresh_token", undefined, undefined,
                    JSON.parse(localStorage.getItem("user") ?? "{}").refresh_token, null
                );
                if (data) {
                    consecutiveFailures = 0;
                    updateStorage(data);
                } else if (localStorage.getItem("user")) {
                    // Le refresh a échoué (token expiré ?), tenter avec l'access_token
                    const accessToken = JSON.parse(localStorage.getItem("user")).access_token;
                    const fallback = await ApiAuthCtrl.login("access_token", undefined, undefined, null, accessToken);
                    if (fallback.data) {
                        consecutiveFailures = 0;
                        updateStorage(fallback.data);
                    }
                    // Si les deux échouent, on ne déconnecte PAS ici — le polling normal s'en chargera
                }
                startPolling(FOREGROUND_INTERVAL);
            }
        }

        async function initSession() {
            if (localStorage.getItem("user") !== null) {
                const token = JSON.parse(localStorage.getItem("user")).access_token;
                const { data, error } = await ApiAuthCtrl.login("access_token", undefined, undefined, null, token);
                if (data) updateStorage(data);
                else if (error) cleanUser();
            }
            await refreshToken();
        }

        initSession();
        startPolling();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            stopPolling();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Rafraîchit manuellement le compteur (appelable depuis PageAdminUsers après une validation)
    const refreshPendingCount = React.useCallback(async () => {
        if (userRef.current?.instances?.[APP_NAME]?.role === "ADMIN") {
            const { data } = await ApiAdminCtrl.getPendingUsers();
            if (data !== null) setPendingCount(data.length);
        }
    }, []);

    // Recharge les informations utilisateur depuis le serveur
    // et force le renouvellement du token pour que les claims soient à jour
    const refreshUser = React.useCallback(async () => {
        // 1. Forcer un nouveau token (les claims embarquées - rôle, BDD - seront à jour)
        const stored = localStorage.getItem("user");
        if (stored) {
            const storedRefreshToken = JSON.parse(stored).refresh_token;
            const { data: tokenData } = await ApiAuthCtrl.login(
                "refresh_token", undefined, undefined, storedRefreshToken, null
            );
            if (tokenData) updateStorage(tokenData);
        }
        // 2. Rafraîchir l'objet user (instances, databases, groups, role…)
        const { data } = await ApiAuthCtrl.userInfo();
        if (data) setUser(data);
    }, []);

    const value = React.useMemo(
        () => ({ auth, setAuth, user, disconnect, cleanUser, pendingCount, refreshPendingCount, refreshUser, analyticsSummaries }),
        [auth, user, pendingCount, refreshPendingCount, refreshUser, analyticsSummaries]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook utilitaire pour consommer AuthContext */
export function useAuth() {
    const ctx = React.useContext(AuthContext);
    if (!ctx) throw new Error("useAuth doit être utilisé dans AuthProvider");
    return ctx;
}
